/**
 * Route → guide. No UI imports: Node scripts and tests import this file.
 * An unregistered route simply has no "?" in the top bar.
 */
import type { PageHelp } from "./types";
import { offersHelp } from "../pages/ProposalList/help";
import { offerHelp } from "../pages/Proposal/help";

export const HELP_REGISTRY: Record<string, PageHelp> = {
  "/": offersHelp,
  "/offer": offerHelp,
  "/offer/:offerId": offerHelp,
};

function normalize(path: string): string {
  const trimmed = path.replace(/[?#].*$/, "").replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

/** True when `pathname` matches `pattern` (`:param` segments match any one segment). */
export function matchesRoute(pattern: string, pathname: string): boolean {
  const a = normalize(pattern).split("/");
  const b = normalize(pathname).split("/");
  if (a.length !== b.length) return false;
  return a.every((seg, i) => seg.startsWith(":") ? b[i] !== "" : seg === b[i]);
}

const paramCount = (pattern: string) =>
  pattern.split("/").filter((s) => s.startsWith(":")).length;

/** Exact → `:param` pattern (fewest params wins) → longest literal prefix + "/" → null. */
export function helpRouteFor(pathname: string): string | null {
  const path = normalize(pathname);
  if (HELP_REGISTRY[path]) return path;

  const patterns = Object.keys(HELP_REGISTRY)
    .filter((p) => p.includes(":") && matchesRoute(p, path))
    .sort((x, y) => paramCount(x) - paramCount(y));
  if (patterns[0]) return patterns[0];

  const prefixes = Object.keys(HELP_REGISTRY)
    .filter((p) => !p.includes(":") && p !== "/" && path.startsWith(p + "/"))
    .sort((x, y) => y.length - x.length);
  return prefixes[0] ?? null;
}

export function helpForPath(pathname: string): PageHelp | null {
  const route = helpRouteFor(pathname);
  return route ? HELP_REGISTRY[route] ?? null : null;
}
